import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Users, 
  UserPlus, 
  Shield, 
  Eye, 
  Edit3, 
  Trash2, 
  MoreVertical,
  Crown,
  CheckCircle,
  XCircle,
  Mail
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const TeamManagement = () => {
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  
  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    role: "lasare"
  });

  const companyId = user?.active_company_id;

  const fetchMembers = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/companies/${companyId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data);
    } catch (error) {
      toast.error(t('team.errorFetchingMembers'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/users/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableRoles(response.data.roles);
    } catch (error) {
      console.error("Could not fetch roles");
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchRoles();
  }, [companyId]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) {
      toast.error(t('team.enterEmail'));
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/companies/${companyId}/invite`,
        { email: inviteForm.email, role: inviteForm.role },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(response.data.message);
      setInviteDialogOpen(false);
      setInviteForm({ email: "", name: "", role: "lasare" });
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('team.inviteFailed'));
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedMember) return;
    
    try {
      await axios.put(
        `${API}/companies/${companyId}/members/${selectedMember.id}/role`,
        { role: selectedMember.role },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(t('team.roleUpdated'));
      setEditDialogOpen(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('team.updateFailed'));
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm(t('team.confirmRemove'))) return;
    
    try {
      await axios.delete(
        `${API}/companies/${companyId}/members/${memberId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(t('team.memberRemoved'));
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('team.removeFailed'));
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: (
        <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
          <Crown size={12} className="mr-1" />
          {t('team.roles.admin')}
        </Badge>
      ),
      revisor: (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
          <Edit3 size={12} className="mr-1" />
          {t('team.roles.accountant')}
        </Badge>
      ),
      lasare: (
        <Badge className="bg-slate-100 text-slate-700 border-slate-200">
          <Eye size={12} className="mr-1" />
          {t('team.roles.viewer')}
        </Badge>
      )
    };
    return badges[role] || <Badge>{role}</Badge>;
  };

  const getRoleDescription = (role) => {
    const descriptions = {
      admin: t('team.roleDesc.admin'),
      revisor: t('team.roleDesc.accountant'),
      lasare: t('team.roleDesc.viewer')
    };
    return descriptions[role] || "";
  };

  // Check if current user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center max-w-md">
          <Shield className="mx-auto mb-4 text-slate-400" size={48} />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            {t('team.accessDenied')}
          </h2>
          <p className="text-slate-500">
            {t('team.adminOnlyAccess')}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="team-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('team.title')}</h1>
          <p className="text-slate-500 mt-1">{t('team.subtitle')}</p>
        </div>
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="invite-member-btn">
              <UserPlus size={16} className="mr-2" />
              {t('team.inviteMember')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('team.inviteMember')}</DialogTitle>
              <DialogDescription>
                {t('team.inviteDescription')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('common.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="kollega@foretag.se"
                  required
                  data-testid="invite-email"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('team.role')}</Label>
                <Select
                  value={inviteForm.role}
                  onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                >
                  <SelectTrigger data-testid="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex flex-col">
                          <span>{role.label}</span>
                          <span className="text-xs text-slate-500">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-invite-btn">
                  <Mail size={16} className="mr-2" />
                  {t('team.sendInvite')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Role Overview */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="card-base p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Crown className="text-indigo-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{members.filter(m => m.role === 'admin').length}</p>
              <p className="text-xs text-slate-500">{t('team.roles.admin')}</p>
            </div>
          </div>
        </Card>
        <Card className="card-base p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Edit3 className="text-amber-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{members.filter(m => m.role === 'revisor').length}</p>
              <p className="text-xs text-slate-500">{t('team.roles.accountant')}</p>
            </div>
          </div>
        </Card>
        <Card className="card-base p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Eye className="text-slate-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{members.filter(m => m.role === 'lasare').length}</p>
              <p className="text-xs text-slate-500">{t('team.roles.viewer')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Members List */}
      <Card className="card-base" data-testid="members-list">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users size={20} />
            {t('team.members')} ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="mx-auto mb-4 text-slate-300" size={48} />
              <p>{t('team.noMembers')}</p>
            </div>
          ) : (
            <div className="divide-y">
              {members.map((member) => (
                <div key={member.id} className="flex items-center justify-between py-4" data-testid={`member-${member.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center">
                      <span className="text-white font-medium">
                        {member.name?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{member.name}</p>
                        {member.id === user?.id && (
                          <Badge className="bg-indigo-50 text-indigo-600 text-xs">
                            {t('team.you')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getRoleBadge(member.role)}
                    
                    {member.id !== user?.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`member-menu-${member.id}`}>
                            <MoreVertical size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedMember(member);
                              setEditDialogOpen(true);
                            }}
                          >
                            <Edit3 size={14} className="mr-2" />
                            {t('team.changeRole')}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => handleRemoveMember(member.id)}
                          >
                            <Trash2 size={14} className="mr-2" />
                            {t('team.remove')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions Info */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield size={20} />
            {t('team.permissionsOverview')}
          </CardTitle>
          <CardDescription>{t('team.permissionsDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 font-medium">{t('team.permission')}</th>
                  <th className="text-center p-3 font-medium">
                    <div className="flex items-center justify-center gap-1">
                      <Crown size={14} />
                      {t('team.roles.admin')}
                    </div>
                  </th>
                  <th className="text-center p-3 font-medium">
                    <div className="flex items-center justify-center gap-1">
                      <Edit3 size={14} />
                      {t('team.roles.accountant')}
                    </div>
                  </th>
                  <th className="text-center p-3 font-medium">
                    <div className="flex items-center justify-center gap-1">
                      <Eye size={14} />
                      {t('team.roles.viewer')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {[
                  { key: 'manageUsers', label: t('team.permissions.manageUsers') },
                  { key: 'manageCompany', label: t('team.permissions.manageCompany') },
                  { key: 'editBookkeeping', label: t('team.permissions.editBookkeeping') },
                  { key: 'editInvoices', label: t('team.permissions.editInvoices') },
                  { key: 'viewReports', label: t('team.permissions.viewReports') },
                  { key: 'exportData', label: t('team.permissions.exportData') },
                  { key: 'yearEndClosing', label: t('team.permissions.yearEndClosing') },
                ].map(perm => (
                  <tr key={perm.key}>
                    <td className="p-3">{perm.label}</td>
                    <td className="p-3 text-center">
                      <CheckCircle className="mx-auto text-green-500" size={18} />
                    </td>
                    <td className="p-3 text-center">
                      {['manageUsers', 'manageCompany'].includes(perm.key) ? (
                        <XCircle className="mx-auto text-slate-300" size={18} />
                      ) : (
                        <CheckCircle className="mx-auto text-green-500" size={18} />
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {perm.key === 'viewReports' ? (
                        <CheckCircle className="mx-auto text-green-500" size={18} />
                      ) : (
                        <XCircle className="mx-auto text-slate-300" size={18} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('team.changeRole')}</DialogTitle>
            <DialogDescription>
              {t('team.changeRoleDescription')} {selectedMember?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center">
                  <span className="text-white font-medium">
                    {selectedMember.name?.charAt(0)?.toUpperCase() || "?"}
                  </span>
                </div>
                <div>
                  <p className="font-medium">{selectedMember.name}</p>
                  <p className="text-sm text-slate-500">{selectedMember.email}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{t('team.newRole')}</Label>
                <Select
                  value={selectedMember.role}
                  onValueChange={(value) => setSelectedMember({ ...selectedMember, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex flex-col">
                          <span>{role.label}</span>
                          <span className="text-xs text-slate-500">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUpdateRole} className="bg-indigo-600 hover:bg-indigo-700">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
